import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import {
  SparklesIcon,
  RocketLaunchIcon,
  PaintBrushIcon,
  TrophyIcon,
  PhotoIcon,
  VideoCameraIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { ContentItem, SlideDesignVariant } from '../types/content';
import SlidePreview from './SlidePreview';

interface SlideContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: ContentItem) => void;
  item: ContentItem | null;
  sectionId: string;
  sections?: any[];
}

const slideTemplates = [
  {
    id: 'promotional',
    name: 'Promotional',
    icon: RocketLaunchIcon,
    description: 'Promote an event or offering',
    template: {
      title: 'PARTNER WITH US',
      subtitle: 'Exclusive Opportunity',
      description: 'Join Africa\'s most prestigious golf tournament and elevate your brand.',
      background: {
        type: 'image' as const,
        url: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1920'
      },
      media: {
        type: 'image' as const,
        url: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800'
      },
      buttonText: 'Learn More',
      buttonLink: '/partners',
      buttonVariant: 'primary' as const,
      designVariant: 'classic' as SlideDesignVariant
    }
  },
  {
    id: 'leaderboard',
    name: 'Leaderboard',
    icon: TrophyIcon,
    description: 'Display tournament standings',
    template: {
      title: 'TOURNAMENT LEADERBOARD',
      subtitle: 'Live Scores',
      description: 'Track real-time scores and rankings from the tournament',
      background: {
        type: 'image' as const,
        url: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1920'
      },
      media: {
        type: 'youtube' as const,
        url: 'dQw4w9WgXcQ',
        autoplay: false,
        controls: true
      },
      buttonText: 'View Full Leaderboard',
      buttonLink: '/leaderboard',
      buttonVariant: 'primary' as const,
      designVariant: 'modern' as SlideDesignVariant
    }
  },
  {
    id: 'venue',
    name: 'Venue Showcase',
    icon: PhotoIcon,
    description: 'Showcase beautiful venue',
    template: {
      title: 'DISCOVER PARADISE',
      subtitle: 'Pristine Beaches',
      description: 'Experience the stunning beauty of our championship golf course',
      background: {
        type: 'image' as const,
        url: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1920'
      },
      media: {
        type: 'image' as const,
        url: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800'
      },
      buttonText: 'Explore Venue',
      buttonLink: '/about',
      buttonVariant: 'primary' as const,
      designVariant: 'minimalist' as SlideDesignVariant
    }
  },
  {
    id: 'video',
    name: 'Video Feature',
    icon: VideoCameraIcon,
    description: 'Feature video content',
    template: {
      title: 'RICH HERITAGE',
      subtitle: 'Cultural Experience',
      description: 'Immerse yourself in the traditions and culture of championship golf',
      background: {
        type: 'image' as const,
        url: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1920'
      },
      media: {
        type: 'youtube' as const,
        url: 'dQw4w9WgXcQ',
        autoplay: false,
        controls: true
      },
      buttonText: 'Watch Video',
      buttonLink: '#',
      buttonVariant: 'primary' as const,
      designVariant: 'bold' as SlideDesignVariant
    }
  }
];

const designVariants = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional two-column layout',
    icon: SparklesIcon
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Contemporary overlay design',
    icon: RocketLaunchIcon
  },
  {
    id: 'minimalist',
    name: 'Minimalist',
    description: 'Clean and simple',
    icon: PaintBrushIcon
  },
  {
    id: 'bold',
    name: 'Bold',
    description: 'Eye-catching full-width',
    icon: TrophyIcon
  }
];

export default function SlideContentModal({
  isOpen,
  onClose,
  onSave,
  item,
  sectionId,
  sections
}: SlideContentModalProps) {
  const [formData, setFormData] = useState<ContentItem>({
    id: crypto.randomUUID(),
    title: '',
    type: 'slide',
    content: '',
    status: 'Draft'
  });
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (item) {
      let content = item.content;
      if (typeof content === 'string') {
        try {
          content = JSON.parse(content);
        } catch (e) {
          content = content.replace(/^"|"$/g, '');
        }
      }

      setFormData({
        ...item,
        content: typeof content === 'object' ? JSON.stringify(content, null, 2) : content
      });
    } else {
      const defaultSlide = slideTemplates[0].template;
      setFormData({
        id: crypto.randomUUID(),
        title: 'New Slide',
        type: 'slide',
        content: JSON.stringify(defaultSlide, null, 2),
        status: 'Draft'
      });
    }
  }, [item, sectionId]);

  const handleTemplateSelect = (template: any) => {
    setFormData({
      ...formData,
      title: template.title,
      content: JSON.stringify(template, null, 2)
    });
  };

  const handleDesignVariantSelect = (variant: string) => {
    try {
      const currentContent = JSON.parse(formData.content);
      currentContent.designVariant = variant;
      setFormData({
        ...formData,
        content: JSON.stringify(currentContent, null, 2)
      });
    } catch (e) {
      console.error('Error updating design variant:', e);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let processedContent = formData.content;

    if (typeof formData.content === 'string') {
      try {
        const parsed = JSON.parse(formData.content);
        processedContent = JSON.stringify(parsed);
      } catch (e) {
        console.error('Error parsing JSON content:', e);
        processedContent = JSON.stringify(formData.content);
      }
    }

    const updatedFormData = {
      ...formData,
      content: processedContent
    };

    onSave(updatedFormData);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                    {item ? 'Edit Slide' : 'Add New Slide'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
                    <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
                      <Tab className={({ selected }) =>
                        `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors
                        ${selected
                          ? 'bg-white text-golf-orange shadow'
                          : 'text-gray-600 hover:bg-white/50 hover:text-gray-800'
                        }`
                      }>
                        Templates
                      </Tab>
                      <Tab className={({ selected }) =>
                        `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors
                        ${selected
                          ? 'bg-white text-golf-orange shadow'
                          : 'text-gray-600 hover:bg-white/50 hover:text-gray-800'
                        }`
                      }>
                        Design
                      </Tab>
                      <Tab className={({ selected }) =>
                        `w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-colors
                        ${selected
                          ? 'bg-white text-golf-orange shadow'
                          : 'text-gray-600 hover:bg-white/50 hover:text-gray-800'
                        }`
                      }>
                        Custom JSON
                      </Tab>
                    </Tab.List>

                    <Tab.Panels>
                      {/* Templates Tab */}
                      <Tab.Panel>
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          {slideTemplates.map((template) => {
                            const Icon = template.icon;
                            return (
                              <button
                                key={template.id}
                                type="button"
                                onClick={() => handleTemplateSelect(template.template)}
                                className="p-4 border-2 border-gray-200 rounded-xl hover:border-golf-orange hover:shadow-md transition-all text-left group"
                              >
                                <div className="flex items-start gap-3">
                                  <div className="p-2 bg-golf-orange/10 rounded-lg group-hover:bg-golf-orange/20 transition-colors">
                                    <Icon className="w-6 h-6 text-golf-orange" />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-1">{template.name}</h4>
                                    <p className="text-sm text-gray-500">{template.description}</p>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {/* Preview */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Preview
                          </label>
                          <div className="border border-gray-300 rounded-lg p-2 bg-gray-50">
                            {(() => {
                              try {
                                const slideContent = JSON.parse(formData.content);
                                return <SlidePreview slide={slideContent} />;
                              } catch (e) {
                                return (
                                  <div className="p-4 bg-red-50 text-red-600 rounded-lg text-center">
                                    Invalid slide format
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        </div>
                      </Tab.Panel>

                      {/* Design Tab */}
                      <Tab.Panel>
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                              Design Variant
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              {designVariants.map((variant) => {
                                const Icon = variant.icon;
                                const currentVariant = (() => {
                                  try {
                                    return JSON.parse(formData.content).designVariant || 'classic';
                                  } catch {
                                    return 'classic';
                                  }
                                })();
                                const isSelected = currentVariant === variant.id;

                                return (
                                  <button
                                    key={variant.id}
                                    type="button"
                                    onClick={() => handleDesignVariantSelect(variant.id)}
                                    className={`p-4 border-2 rounded-xl transition-all text-left ${
                                      isSelected
                                        ? 'border-golf-orange bg-golf-orange/5'
                                        : 'border-gray-200 hover:border-golf-orange/50'
                                    }`}
                                  >
                                    <div className="flex items-start gap-3">
                                      <div className={`p-2 rounded-lg ${
                                        isSelected ? 'bg-golf-orange/20' : 'bg-gray-100'
                                      }`}>
                                        <Icon className={`w-5 h-5 ${
                                          isSelected ? 'text-golf-orange' : 'text-gray-600'
                                        }`} />
                                      </div>
                                      <div>
                                        <h4 className="font-medium text-gray-900">{variant.name}</h4>
                                        <p className="text-xs text-gray-500 mt-1">{variant.description}</p>
                                      </div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Hide Image Toggle */}
                          <div className="flex items-center justify-between p-4 border border-gray-300 rounded-lg bg-gray-50">
                            <div>
                              <label className="text-sm font-medium text-gray-700">
                                Hide Image
                              </label>
                              <p className="text-xs text-gray-500 mt-1">
                                Hide the right column image/video and center the content
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                try {
                                  const currentContent = JSON.parse(formData.content);
                                  currentContent.hideImage = !currentContent.hideImage;
                                  setFormData({
                                    ...formData,
                                    content: JSON.stringify(currentContent, null, 2)
                                  });
                                } catch (e) {
                                  console.error('Error toggling hideImage:', e);
                                }
                              }}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 ${
                                (() => {
                                  try {
                                    return JSON.parse(formData.content).hideImage ? 'bg-golf-orange' : 'bg-gray-300';
                                  } catch {
                                    return 'bg-gray-300';
                                  }
                                })()
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  (() => {
                                    try {
                                      return JSON.parse(formData.content).hideImage ? 'translate-x-6' : 'translate-x-1';
                                    } catch {
                                      return 'translate-x-1';
                                    }
                                  })()
                                }`}
                              />
                            </button>
                          </div>

                          {/* Preview */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Preview
                            </label>
                            <div className="border border-gray-300 rounded-lg p-2 bg-gray-50">
                              {(() => {
                                try {
                                  const slideContent = JSON.parse(formData.content);
                                  return <SlidePreview slide={slideContent} />;
                                } catch (e) {
                                  return (
                                    <div className="p-4 bg-red-50 text-red-600 rounded-lg text-center">
                                      Invalid slide format
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          </div>
                        </div>
                      </Tab.Panel>

                      {/* Custom JSON Tab */}
                      <Tab.Panel>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Slide Title
                            </label>
                            <input
                              type="text"
                              value={formData.title}
                              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Slide Content (JSON)
                            </label>
                            <div className="text-xs text-gray-500 mb-2 space-y-1">
                              <p><strong>Required:</strong> title, subtitle, description, background, buttonText, buttonLink, buttonVariant</p>
                              <p><strong>Optional:</strong> media, designVariant (classic | modern | minimalist | bold), hideImage (boolean)</p>
                            </div>

                            <textarea
                              value={formData.content}
                              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                              rows={12}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange font-mono text-sm"
                            />
                          </div>

                          {/* Preview */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Preview
                            </label>
                            <div className="border border-gray-300 rounded-lg p-2 bg-gray-50">
                              {(() => {
                                try {
                                  const slideContent = JSON.parse(formData.content);
                                  return <SlidePreview slide={slideContent} />;
                                } catch (e) {
                                  return (
                                    <div className="p-4 bg-red-50 text-red-600 rounded-lg text-center">
                                      Invalid JSON format
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          </div>
                        </div>
                      </Tab.Panel>
                    </Tab.Panels>
                  </Tab.Group>

                  {/* Common Fields */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
                          </label>
                          <select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Published' | 'Draft' })}
                            className="rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                          >
                            <option value="Draft">Draft</option>
                            <option value="Published">Published</option>
                          </select>
                        </div>

                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-6 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90"
                          >
                            {item ? 'Save Changes' : 'Add Slide'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
